import React from "react";
import { stockwaveClassNames } from "../../../utils/widgets/stockwave";

/** Vehicle Metadata for Stockwave integration.  Can be from SRP or VDP item info
 * * returns `null` if `isStockwaveEnabled` is falsey
 * @param {{vehicleData: StockwaveVehicleData, isStockwaveEnabled: boolean | undefined}} props `isStockwaveEnabled` <-- `mpConfig.enableStockwave` setting
 */
export const StockwaveVehicleInfo = ({vehicleData={}, isStockwaveEnabled}) => {
    if (!isStockwaveEnabled) return null;
    const {
        vin, vIN, 
        stockNumber, 
        year, 
        make, 
        model, 
        id, listingId,
        trim, series,
        vehicleZip, zip,
        engine, drivetrain, drive, transmission,
        odometer, mileageDesc, odometerNotes,
        exteriorColor, extColor,
        intColor, intType,
        crGrade, ratingValue, 
        conditionReportUrl, cRLink,
        lotNumber,
        timesRun, distance
    } = vehicleData;
    
    const vdpUrl = (id || listingId) ? `${window.location.origin}/item/${id || listingId}` : '';

    const stockwaveVehicleData = {
        Vin: vin || vIN || '',
        Year: year || '',
        Make: make || '',
        Model: model || '',
        Series: series || trim || '',
        Trim: trim || series || '',
        PickupZip: vehicleZip || zip || '',
        Engine: engine || '',
        Drivetrain: drivetrain || drive || '',
        Transmission: transmission || '',
        Odometer: odometer || '',
        OdometerUnitOfMeasure: mileageDesc || odometerNotes || 'mi',
        Color: exteriorColor || extColor || '',
        InteriorColor: intColor || '',
        InteriorType: intType || '',
        CrScore: crGrade ?? ratingValue ?? 0,
        CrUrl: conditionReportUrl || cRLink || '',
        VdpUrl: vdpUrl,
        Lane: lotNumber || '',
        Run: timesRun ?? 0,
        Distance: distance,
    }
    
    return (
        <pre style={{ width: 0, display: 'none' }} className={stockwaveClassNames.srpVehicleInfo} >{JSON.stringify(stockwaveVehicleData)}</pre>
    )
}

/** Key vehicle data for stockwave integration.  Can be from SRP or VDP item info
 * @typedef {Object} StockwaveVehicleData
 * @property {number|undefined} id - ID of vehicle (_Elastic Search_)
 * @property {number|undefined} listingId - ID of vehicle (_Elastic Search_)
 * @property {string|undefined} stockNumber - Stock number of the vehicle
 * @property {string|undefined} lotNumber - Lot number of the vehicle
 * @property {number|undefined} timesRun - # of times vehicle has run (_AWG_)
 *
 * @property {string|undefined} vin - VIN of vehicle (_AWG_)
 * @property {string|undefined} vIN - VIN of vehicle (_Elastic Search_)
 * @property {number|undefined} odometer Odometer reading (_AWG_)
 * @property {number|undefined} mileage Odometer reading (_Elastic Search_)
 * @property {'mi'|'km'|undefined} mileageDesc Odometer reading (_Elastic Search_)
 * @property {'mi'|'km'|undefined} odometerNotes Odometer reading (_AWG_)
 * 
 * @property {number|undefined} year - Year of the vehicle
 * @property {string|undefined} make - Make of the vehicle
 * @property {string|undefined} model - Model of the vehicle
 * @property {string|undefined} trim - Trim of the vehicle (_AWG_)
 * @property {string|undefined} series - Series  of the vehicle (_Elastic Search_)
 * 
 * @property {string|undefined} engine - Engine of the vehicle (_AWG_ | _Elastic Search_)
 * @property {string|undefined} drivetrain - Drivetrain of the vehicle (_AWG_)
 * @property {string|undefined} drive - Drivetrain of the vehicle (_Elastic Search_)
 * @property {string|undefined} transmission - Transmission of the vehicle (_AWG_ | _Elastic Search_)
 * 
 * @property {string|undefined} exteriorColor - Ext Color of the vehicle (_Elastic Search_)
 * @property {string|undefined} extColor - Ext Color of the vehicle (_Elastic Search_)
 * @property {string|undefined} intColor - Int Color of the vehicle (_AWG_)
 * @property {string|undefined} intType - Int Type of the vehicle (_AWG_)
 *
 * @property {string|undefined} vehicleZip - Zip code of the vehicle (_AWG_)
 * @property {string|undefined} zip - Zip code of the vehicle (_Elastic Search_)
 * @property {number|undefined} crGrade - CR grade of the vehicle (_AWG_)
 * @property {number|undefined} ratingValue - CR grade of the vehicle (_AWG_)
 * @property {string|undefined} conditionReportUrl - CR URL of the vehicle (_AWG_)
 * @property {string|undefined} cRLink - CR URL of the vehicle (_Elastic Search_)
 * 
 * @property {number|undefined} distance - Distance from the vehicle (_Elastic Search_ computed)
 */

/** @type {StockwaveVehicleData} */